'use client';
import React, { useState, useEffect } from 'react';
import { Act } from '@/data/scheduleData';
import { Timer } from 'lucide-react';

interface CountdownBannerProps {
  act: Act;
  now: Date;
  dayDate: string;
}

function getSecondsUntil(act: Act, dayDate: string, now: Date): number {
  const [day, month, year] = dayDate.split('.').map(Number);
  const [startH, startM] = act.startTime.split(':').map(Number);
  const startDate = new Date(year, month - 1, day, startH, startM);
  return Math.max(0, Math.floor((startDate.getTime() - now.getTime()) / 1000));
}

export default function CountdownBanner({ act, now, dayDate }: CountdownBannerProps) {
  const [seconds, setSeconds] = useState(() => getSecondsUntil(act, dayDate, now));

  // Reset seconds whenever the act or day changes
  useEffect(() => {
    setSeconds(getSecondsUntil(act, dayDate, new Date()));
  }, [act.id, dayDate]);

  // Tick every second using real wall-clock time to avoid drift
  useEffect(() => {
    const interval = setInterval(() => {
      setSeconds(getSecondsUntil(act, dayDate, new Date()));
    }, 1000);
    return () => clearInterval(interval);
  }, [act.id, dayDate]);

  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;

  if (seconds === 0) return null;

  return (
    <div className="flex items-center gap-3 bg-accent/10 border border-accent/30 rounded-xl px-4 py-3">
      <Timer size={16} className="text-accent flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <span className="text-xs font-medium text-muted-foreground">Nächster Act: </span>
        <span className="text-sm font-semibold text-foreground">{act.name}</span>
        <span className="text-xs text-muted-foreground ml-1">({act.stage})</span>
      </div>
      <div className="font-mono text-sm font-bold text-accent tabular-nums">
        {h > 0 && `${String(h).padStart(2, '0')}:`}
        {String(m).padStart(2, '0')}:{String(s).padStart(2, '0')}
      </div>
    </div>
  );
}